import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import {
  CaseBattleAnimationEnum,
  CaseBattlesSlotsVariants,
  ICaseBattle,
} from '../interfaces/case-battles.interface';
import { NgParticlesService, NgxParticlesModule } from '@tsparticles/angular';
import { SessionService } from '@core/services/session.service';
import { Engine, ISourceOptions } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { BalanceIconComponent } from '../../../core/components/balance-icon/balance-icon.component';
import { ContextMenuService } from '@core/services/context-menu.service';
import NumberFlow from 'number-flow';
import {
  getDropsSumByBattle,
  getEquityModeSum,
  getUserDropsCostBySlot,
} from '../utils/utils';
import { fadeInAnimation } from '@core/utils/animations.util';
import { CaseBattlesService } from '../case-battles.service';

@Component({
  selector: 'app-cb-battle-middle-results-player',
  imports: [NgxParticlesModule, BalanceIconComponent],
  templateUrl: './cb-battle-middle-results-player.component.html',
  styleUrl: './cb-battle-middle-results-player.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  animations: [fadeInAnimation],
})
export class CbBattleMiddleResultsPlayerComponent {
  particlesOptions: ISourceOptions = {
    fullScreen: { enable: false },
    particles: {
      number: {
        value: 20,
      },
      color: {
        value: ['#ffffff'],
      },
      move: {
        direction: 'none',
        enable: true,
        outModes: {
          default: 'out',
        },
        random: true,
        speed: 1.5,
        straight: false,
      },
      opacity: {
        value: { min: 0.17, max: 0.5 },
      },
      size: {
        value: { min: 1, max: 1.2 },
      },
      wobble: {
        enable: false,
      },
    },
  };

  private ngParticlesService = inject(NgParticlesService);
  private caseBattlesService = inject(CaseBattlesService);
  public sessionService = inject(SessionService);
  public contextMenuService = inject(ContextMenuService);

  battle = input.required<ICaseBattle>();
  slot = input.required<number>();
  animation = input.required<keyof typeof CaseBattleAnimationEnum>();

  isShowEarned = signal(false);

  player = computed(() => {
    return (
      this.battle().participants.find((p) => p.slot === this.slot()) ?? null
    );
  });

  totalDropsRef = viewChild.required<ElementRef<NumberFlow>>('totalDropsRef');
  totalEarnedRef = viewChild.required<ElementRef<NumberFlow>>('totalEarnedRef');

  isWinner = computed(() => {
    return this.battle()
      .winners.map((player) => player.slot)
      .includes(this.slot() as CaseBattlesSlotsVariants);
  });

  playerFinalSum = computed(() => {
    const { type, mode } = this.battle();

    const isTeamMode = type === 'team';
    const isEquityMode = mode === 'equity';

    return isEquityMode
      ? getEquityModeSum(this.battle())
      : isTeamMode
      ? getDropsSumByBattle(this.battle()) / 2
      : getDropsSumByBattle(this.battle());
  });

  ngAfterViewInit() {
    const refs = [this.totalDropsRef(), this.totalEarnedRef()];

    refs.forEach((ref) => {
      ref.nativeElement.format = {
        minimumIntegerDigits: 1,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };
      ref.nativeElement.locales = 'en-US';
      ref.nativeElement.transformTiming = {
        duration: 300,
        //easing: 'linear',
      };
      ref.nativeElement.spinTiming = {
        duration: 300,
        //easing: 'linear',
      };
      ref.nativeElement.opacityTiming = {
        duration: 300,
        //easing: 'linear',
      };

      ref.nativeElement.update(0);
    });

    this.totalDropsRef()?.nativeElement.update(
      getUserDropsCostBySlot(this.battle().rounds, this.slot())
    );

    const showEarnedTimeout =
      this.caseBattlesService.getBattleSettings(this.battle())
        .BATTLE_RESULTS_TIMEOUT / 2;
    setTimeout(() => {
      this.isShowEarned.set(true);
    }, showEarnedTimeout);

    const updateEarnedTimeout = showEarnedTimeout + 300;
    setTimeout(() => {
      this.totalEarnedRef()?.nativeElement.update(
        this.isWinner() ? this.playerFinalSum() : 0
      );
    }, updateEarnedTimeout);
  }

  ngOnInit() {
    this.ngParticlesService.init(async (engine: Engine) => {
      await loadSlim(engine);
    });
  }
}
